import { type FC, type RefObject, useEffect } from 'react';
import { Loader2, Save } from 'lucide-react';
import { useMetadataPanelForm } from '@/components/inspector/metadata-accordion/metadata-panel-form';
import { Button } from '@/components/ui/button';
import { useTranslations } from 'next-intl';

/**
 * The Metadata panel's save bar.
 *
 * It sits in the Inspector's own flex column, below the tab pane rather than inside the metadata
 * section, so it saves everything the panel's form holds and the pane shrinks to make room for it
 * instead of being overlaid. That is why nothing here is sticky, and why the section it used to live
 * in no longer has to stretch or give up its overflow clipping.
 */
export const MetadataSaveBar: FC<{ scrollContainerRef: RefObject<HTMLElement | null> }> = ({ scrollContainerRef }) => {
  const tCommon = useTranslations('common');
  const tInspectorMetadata = useTranslations('inspector.metadata');
  const { formEdited, isLoading, metadataListIsFetching, submit, revert, editedField, getEditedFieldRow } =
    useMetadataPanelForm();

  // Appearing takes the pane's bottom edge with it: the fields keep their scroll offset while the
  // space around them shrinks, so the row that sat at the old edge can end up below the new one. Bring
  // the edited row back into view by exactly the amount it falls short — the row holds the control and
  // its validation message, so both are covered — and do nothing when it is already visible, which is
  // what keeps repeated edits from walking the pane down.
  useEffect(() => {
    if (!formEdited) {
      return;
    }

    const scroller = scrollContainerRef.current;
    const row = getEditedFieldRow();

    if (!scroller || !row) {
      return;
    }

    // Measured against a line 8px above the pane's edge, not the edge itself, so the row clears the
    // bar with a little air rather than ending flush against it.
    const shortfall = row.getBoundingClientRect().bottom - (scroller.getBoundingClientRect().bottom - 8);

    if (shortfall > 0) {
      scroller.scrollTop += shortfall;
    }
  }, [formEdited, editedField, getEditedFieldRow, scrollContainerRef]);

  if (!formEdited) {
    return null;
  }

  return (
    <div
      data-cy="metadata-save-bar"
      className="flex h-12 max-h-12 min-h-12 items-center justify-between gap-x-2 bg-status-warning px-3 py-2 dark:text-text-inverted"
    >
      {/* The bar's height is fixed so the pane can hand it exactly that much room. A long
          translation of this string would otherwise either push the buttons out of the bar or be
          clipped mid-line, so it wraps and clamps inside its own box instead. */}
      <div className="line-clamp-2 min-w-0 wrap-anywhere">{tInspectorMetadata('unsavedChanges')}</div>
      <div className="flex flex-row">
        <Button
          variant="ghost"
          type="button"
          data-cy="metadata-revert-button"
          className="font-bold hover:bg-transparent dark:text-text-inverted dark:hover:bg-transparent dark:hover:text-text-inverted"
          onClick={revert}
        >
          {tInspectorMetadata('revert')}
        </Button>
        <Button
          type="button"
          variant="default"
          data-cy="metadata-save-button"
          className="flex w-full justify-center gap-x-2 rounded-xl bg-status-warning-dark px-3 py-1.5 font-bold text-text-base hover:bg-status-warning-dark dark:bg-status-warning-dark dark:hover:bg-status-warning-dark"
          disabled={metadataListIsFetching || isLoading}
          onClick={submit}
          {...(isLoading && { iconClassName: 'animate-spin' })}
          icon={isLoading ? Loader2 : Save}
        >
          {isLoading ? tCommon('saving') : tCommon('save')}
        </Button>
      </div>
    </div>
  );
};
